import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Mail, Phone, Calendar, Edit } from 'lucide-react';
import { Contact } from '../../types';
import Card, { CardBody } from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import api from '../../services/api';

const statusColors = {
  new: 'bg-blue-50 text-blue-700',
  contacted: 'bg-yellow-50 text-yellow-700',
  enrolled: 'bg-green-50 text-green-700',
  closed: 'bg-gray-200 text-gray-600',
};

export default function ContactRequests() {
  const { t } = useTranslation('admin');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<Contact['status']>('new');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await api.get('/admin/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (contact: Contact) => {
    setSelectedContact(contact);
    setNotes(contact.notes || '');
    setStatus(contact.status);
    setIsModalOpen(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContact) return;
    setIsSubmitting(true);

    try {
      await api.put(`/admin/contacts/${selectedContact._id}`, { status, notes });
      setContacts((prev) =>
        prev.map((c) => (c._id === selectedContact._id ? { ...c, status, notes } : c))
      );
      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to update contact:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredContacts = contacts.filter((contact) => {
    return filter === 'all' || contact.status === filter;
  });

  const filterLabels: Record<string, string> = {
    all: t('contacts.filterAll'),
    new: t('contacts.filterNew'),
    contacted: t('contacts.filterContacted'),
    enrolled: t('contacts.filterEnrolled'),
    closed: t('contacts.filterClosed'),
  };

  const statusLabels: Record<string, string> = {
    new: t('contacts.statusNew'),
    contacted: t('contacts.statusContacted'),
    enrolled: t('contacts.statusEnrolled'),
    closed: t('contacts.statusClosed'),
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('contacts.title')}</h1>
        <p className="text-gray-500">{t('contacts.subtitle')}</p>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {['all', 'new', 'contacted', 'enrolled', 'closed'].map((tab) => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === tab
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-100'
            }`}
          >
            {filterLabels[tab]}
            {tab === 'new' && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-white text-primary-600 rounded-full">
                {contacts.filter((c) => c.status === 'new').length}
              </span>
            )}
          </button>
        ))}
      </div>

      {filteredContacts.length === 0 ? (
        <Card>
          <CardBody>
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('contacts.noContacts')}</h3>
              <p className="text-gray-500">
                {filter === 'all'
                  ? t('contacts.noContactsAll')
                  : t('contacts.noContactsFiltered', { filter: filterLabels[filter] })}
              </p>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredContacts.map((contact) => (
            <Card key={contact._id}>
              <CardBody>
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{contact.name}</h3>
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          statusColors[contact.status]
                        }`}
                      >
                        {statusLabels[contact.status]}
                      </span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Mail className="w-4 h-4 mr-2 text-gray-500" />
                        {contact.email}
                      </div>
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 mr-2 text-gray-500" />
                        {contact.phone}
                      </div>
                    </div>
                    {contact.courseInterest && (
                      <p className="text-sm text-primary-400 mt-2">
                        {t('contacts.interestedIn', { course: contact.courseInterest })}
                      </p>
                    )}
                    {contact.message && (
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">{contact.message}</p>
                    )}
                    <div className="flex items-center text-xs text-gray-500 mt-2">
                      <Calendar className="w-3 h-3 mr-1" />
                      {new Date(contact.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(contact)}>
                    <Edit className="w-4 h-4 mr-2" />
                    {t('common.update')}
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Update Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('contacts.updateContact')}
      >
        {selectedContact && (
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">{selectedContact.name}</h4>
              <p className="text-sm text-gray-500">{selectedContact.email}</p>
              <p className="text-sm text-gray-500">{selectedContact.phone}</p>
              {selectedContact.courseInterest && (
                <p className="text-sm text-primary-400 mt-2">
                  {t('contacts.interest', { course: selectedContact.courseInterest })}
                </p>
              )}
              {selectedContact.message && (
                <p className="text-sm text-gray-500 mt-2">{selectedContact.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">{t('contacts.statusLabel')}</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as Contact['status'])}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="new">{t('contacts.statusNew')}</option>
                <option value="contacted">{t('contacts.statusContacted')}</option>
                <option value="enrolled">{t('contacts.statusEnrolled')}</option>
                <option value="closed">{t('contacts.statusClosed')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">{t('contacts.notesLabel')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder={t('contacts.notesPlaceholder')}
              />
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" isLoading={isSubmitting}>
                {t('common.update')}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
